import IShiftrObjectModelBase from "../IShiftrObjectModelBase";
import { IBaseBreakEntity, IBaseSubshiftEntity, EntityCreatedOrUpdatedByInfo } from "sh-models";

type ShiftWorkingType = "Working";
type ShiftAbsenceType = "Absence";
type ShiftSmartScheduleType = "SmartSchedule";

export type ShiftType = ShiftWorkingType | ShiftAbsenceType | ShiftSmartScheduleType;

export const ShiftTypes = {
    Working: "Working" as ShiftWorkingType,
    Absence: "Absence" as ShiftAbsenceType,
    SmartSchedule: "SmartSchedule" as ShiftSmartScheduleType
};

type ShiftStateActiveType = "Active";
type ShiftStatePendingType = "Pending";
type ShiftStateDeletedType = "Deleted";
export type ShiftState = ShiftStateActiveType | ShiftStatePendingType | ShiftStateDeletedType;

export const ShiftStates = {
    Active: "Active" as ShiftStateActiveType,
    Pending: "Pending" as ShiftStatePendingType,
    Deleted: "Deleted" as ShiftStateDeletedType
};

/**
 * Interface to represent Core Shift Entity with a generic DateTime type - will be used by both service and client models of Shifts and OpenShifts
 */
export default interface IBaseShiftData<DateTimeType> extends IShiftrObjectModelBase  {
    /** Start time for the shift */
    startTime: DateTimeType;
    /** End time for the shift */
    endTime: DateTimeType;
    /** The shift type "Working" | "Absence" */
    shiftType: ShiftType;
    /** Current state of the shift */
    state: ShiftState;
    /** Title for the shift (optional) */
    title?: string;
    /** Member id associated with the shift (optional) */
    memberId?: string;
    /** Notes for the shift (optional) */
    notes?: string;
    /** Theme associated with the shift (optional) */
    theme?: string;
    /** List of breaks in the shift (optional) */
    breaks?: IBaseBreakEntity<DateTimeType>[];
    /** List of subshifts in the shift (optional) */
    subshifts?: IBaseSubshiftEntity<DateTimeType>[];
    /**  Time of reason request id (optional) */
    timeOffReasonId?: string;
    /** The IDs of the tags assigned to the shift (optional) */
    tagIds?: string[];
    /** Is shift published (optional) */
    isPublished?: boolean;
    /** Team Id (optional) */
    teamId?: string;
    /** Tenant Id (optional) */
    tenantId?: string;
    /** Will identify shifts request, if the shift is of shift request type */
    shiftRequestId?: string;
    /**
     * The info about the user and time when the shift was last modified.
     */
    lastModifiedBy?: EntityCreatedOrUpdatedByInfo;
    /** Indicates if the shift is in a pre-draft state. Predraft are generated from the SmartScheduling feature.*/
    isPredraft?: boolean;
    /**
     * Server-set marker recording how the shift was originally created. Value `"GenerateScheduleWithHistory"`
     * identifies shifts produced by the Assign Open Shifts feature; the marker is retained even if the shift is
     * later edited (time, theme, notes, etc.), so it always reflects the original creation source. Other values
     * are passed through opaquely for forward-compatibility with future generation sources.
     */
    generationSource?: string;
    /**
     * Server-set flag stamped on AOS-produced shifts (`generationSource === "GenerateScheduleWithHistory"`)
     * when the manager has reassigned the shift to a different member via drag-and-drop after the assignment
     * job completed. Used to count manual rejection of the algorithm's person pick on `Publish_ShareWithTeamComplete`
     * (property `NumAutoAssignedShiftsReassignedAndPublished`). Like `generationSource`, this field is read-only
     * on the client — round-tripped through the model on reads but intentionally omitted from `toJson` so
     * client writes don't echo it back to the server.
     */
    isReassignedAfterGeneration?: boolean;
    /**
     * Server-set flag that becomes `true` the first time an AOS-produced shift
     * (`generationSource === "GenerateScheduleWithHistory"`) is published via the share API, and
     * stays `true` on all subsequent publishes. Used to gate `NumAutoAssignedShiftsPublished` and
     * `NumAutoAssignedShiftsReassignedAndPublished` on `Publish_ShareWithTeamComplete` so those
     * counters only fire on the first publish. Like `generationSource`, this field is read-only on
     * the client — round-tripped through the model on reads but intentionally omitted from `toJson`
     * so client writes don't echo it back to the server.
     */
    isPublishedAfterGeneration?: boolean;
}